import galleryVideo from "../../assets/images/feedback-gallery-video.svg";
import galleryVoice from "../../assets/images/feedback-gallery-voice.svg";
import { useMediaObservation } from "./SurveyObservationContext";
import { useEffect, useId, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ButtonBase from "@mui/material/ButtonBase";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import VideocamRounded from "@mui/icons-material/VideocamRounded";
import MicRounded from "@mui/icons-material/MicRounded";
import type { CompiledSurvey } from "@apruv/survey-contract/compiler";
import type { ProofOfServiceMediaItem } from "../types";

type Configuration = CompiledSurvey["snapshot"]["configuration"];
const mediaType = (item: ProofOfServiceMediaItem) =>
  item.type === "voice" ? "audio" : item.type;
const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(maximum, Math.max(minimum, value));

function ZoomableGalleryImage({
  item,
  unavailable,
  onNavigate,
  framing,
}: {
  item: ProofOfServiceMediaItem;
  unavailable: string;
  onNavigate: (delta: number) => void;
  framing: Configuration["gallery"];
}) {
  const url = item.url ?? item.thumbnailUrl;
  const mediaObservation = useMediaObservation("image", `image:${url}`);
  const frame = useRef<HTMLDivElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const drag = useRef<
    { x: number; y: number; offsetX: number; offsetY: number } | undefined
  >(undefined);
  const swipe = useRef<{ x: number; y: number } | undefined>(undefined);
  const pinch = useRef<{ distance: number; scale: number } | undefined>(
    undefined,
  );
  const lastTap = useRef(0);
  const [failed, setFailed] = useState(false);
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
  const cover = framing.detailFit === "cover";
  const detailRatio = framing.detailAspectRatio ?? 1.1;
  const configuredScale = cover ? (framing.detailZoom ?? 1) : 1;
  const normalized = (scale: number, x: number, y: number) => {
    const nextScale = clamp(scale, 1, 4);
    if (nextScale === 1) return { scale: 1, x: 0, y: 0 };
    const bounds = frame.current?.getBoundingClientRect();
    const maximumX = ((bounds?.width ?? 0) * (nextScale - 1)) / 2;
    const maximumY = ((bounds?.height ?? 0) * (nextScale - 1)) / 2;
    return {
      scale: nextScale,
      x: clamp(x, -maximumX, maximumX),
      y: clamp(y, -maximumY, maximumY),
    };
  };
  const updateScale = (scale: number) =>
    setTransform((current) => normalized(scale, current.x, current.y));
  const pointerDistance = () => {
    const [first, second] = [...pointers.current.values()];
    return first && second
      ? Math.hypot(second.x - first.x, second.y - first.y)
      : 0;
  };
  const endPointer = (event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    const start = swipe.current;
    const wasSinglePointer = pointers.current.size === 1;
    pointers.current.delete(event.pointerId);
    if (pointers.current.size === 1) {
      const remaining = [...pointers.current.values()][0];
      drag.current = {
        x: remaining.x,
        y: remaining.y,
        offsetX: transform.x,
        offsetY: transform.y,
      };
      swipe.current = undefined;
      pinch.current = undefined;
      return;
    }
    if (pointers.current.size > 0) return;
    const movementX = start ? event.clientX - start.x : 0;
    const movementY = start ? event.clientY - start.y : 0;
    if (
      wasSinglePointer &&
      transform.scale === 1 &&
      Math.abs(movementX) > 50 &&
      Math.abs(movementX) > Math.abs(movementY)
    )
      onNavigate(movementX < 0 ? 1 : -1);
    if (
      wasSinglePointer &&
      event.pointerType === "touch" &&
      Math.abs(movementX) < 10 &&
      Math.abs(movementY) < 10
    ) {
      const now = Date.now();
      if (now - lastTap.current < 300) updateScale(transform.scale > 1 ? 1 : 2);
      lastTap.current = now;
    }
    drag.current = undefined;
    swipe.current = undefined;
    pinch.current = undefined;
  };

  if (failed || !url)
    return (
      <Typography role="status" sx={{ color: "#fff", p: 3 }}>
        {unavailable}
      </Typography>
    );
  return (
    <Box
      ref={frame}
      role="group"
      tabIndex={0}
      aria-label={`${item.label}. Zoomable image`}
      data-testid="gallery-image-zoom"
      data-zoom-scale={transform.scale}
      onDoubleClick={(event) => {
        event.preventDefault();
        updateScale(transform.scale > 1 ? 1 : 2);
      }}
      onKeyDown={(event) => {
        if (event.key === "+" || event.key === "=") {
          event.preventDefault();
          updateScale(transform.scale + 0.5);
        } else if (event.key === "-") {
          event.preventDefault();
          updateScale(transform.scale - 0.5);
        } else if (event.key === "0") {
          event.preventDefault();
          updateScale(1);
        }
      }}
      onWheel={(event) => {
        event.preventDefault();
        updateScale(transform.scale + (event.deltaY < 0 ? 0.25 : -0.25));
      }}
      onPointerDown={(event) => {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        event.stopPropagation();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        pointers.current.set(event.pointerId, {
          x: event.clientX,
          y: event.clientY,
        });
        if (pointers.current.size === 1) {
          drag.current = {
            x: event.clientX,
            y: event.clientY,
            offsetX: transform.x,
            offsetY: transform.y,
          };
          swipe.current = { x: event.clientX, y: event.clientY };
        } else if (pointers.current.size === 2) {
          pinch.current = {
            distance: pointerDistance(),
            scale: transform.scale,
          };
          swipe.current = undefined;
        }
      }}
      onPointerMove={(event) => {
        if (!pointers.current.has(event.pointerId)) return;
        event.preventDefault();
        event.stopPropagation();
        pointers.current.set(event.pointerId, {
          x: event.clientX,
          y: event.clientY,
        });
        if (pointers.current.size === 2 && pinch.current) {
          const distance = pointerDistance();
          if (pinch.current.distance > 0)
            updateScale(
              pinch.current.scale * (distance / pinch.current.distance),
            );
        } else if (
          pointers.current.size === 1 &&
          transform.scale > 1 &&
          drag.current
        ) {
          setTransform(
            normalized(
              transform.scale,
              drag.current.offsetX + event.clientX - drag.current.x,
              drag.current.offsetY + event.clientY - drag.current.y,
            ),
          );
        }
      }}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      sx={{
        aspectRatio: cover ? String(detailRatio) : "393 / 357",
        cursor: transform.scale > 1 ? "grab" : "zoom-in",
        maxHeight: cover ? "calc(100dvh - 160px)" : "100dvh",
        overflow: "hidden",
        position: "relative",
        touchAction: "none",
        width: cover
          ? `min(100%, calc((100dvh - 160px) * ${detailRatio}))`
          : "100%",
        "&:focus-visible": { outline: "2px solid white", outlineOffset: -2 },
      }}
    >
      <Box
        component="img"
        src={url}
        alt={item.label}
        draggable={false}
        onLoad={mediaObservation.ready}
        onError={() => {
          setFailed(true);
          mediaObservation.failed();
        }}
        sx={{
          display: "block",
          height: "100%",
          inset: 0,
          maxWidth: "none",
          objectFit: cover ? "cover" : "contain",
          objectPosition: `${(framing.detailFocalX ?? 0.5) * 100}% ${(framing.detailFocalY ?? 0.5) * 100}%`,
          pointerEvents: "none",
          position: "absolute",
          transform: `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${configuredScale * transform.scale})`,
          transformOrigin: `${(framing.detailFocalX ?? 0.5) * 100}% ${(framing.detailFocalY ?? 0.5) * 100}%`,
          userSelect: "none",
          width: "100%",
          willChange: "transform",
        }}
      />
    </Box>
  );
}

function GalleryAsset({
  item,
  detail,
  unavailable,
  framing,
}: {
  item: ProofOfServiceMediaItem;
  detail: boolean;
  unavailable: string;
  framing?: Configuration["gallery"];
}) {
  const [failed, setFailed] = useState(false);
  const url = detail
    ? (item.url ?? (item.type === "image" ? item.thumbnailUrl : undefined))
    : (item.thumbnailUrl ?? (item.type === "image" ? item.url : undefined));
  const mediaObservation = useMediaObservation(
    detail && item.type !== "note" ? item.type : undefined,
    `${item.type}:${url}`,
  );
  if (
    failed ||
    (!url && (detail || item.type === "image")) ||
    (item.type !== "image" && !item.url)
  )
    return (
      <Typography role="status" sx={{ p: 3 }}>
        {unavailable}
      </Typography>
    );
  if (detail && (item.type === "video" || item.type === "voice"))
    return (
      <Box
        component={item.type === "video" ? "video" : "audio"}
        controls
        disablePictureInPicture={item.type === "video"}
        playsInline={item.type === "video"}
        preload="metadata"
        onLoadedMetadata={mediaObservation.ready}
        src={url}
        aria-label={item.label}
        onError={() => {
          setFailed(true);
          mediaObservation.failed();
        }}
        sx={{
          maxWidth: "100%",
          maxHeight: "calc(100dvh - 160px)",
          width: item.type === "video" ? "100%" : undefined,
        }}
      />
    );
  if (!detail && item.type === "video" && item.url && !item.thumbnailUrl)
    return (
      <Box
        aria-label={item.label}
        component="video"
        muted
        playsInline
        preload="metadata"
        src={item.url}
        onError={() => {
          setFailed(true);
          mediaObservation.failed();
        }}
        sx={{
          display: "block",
          height: 200,
          objectFit: "cover",
          width: "100%",
        }}
      />
    );
  if (
    url &&
    detail &&
    item.type === "image" &&
    framing?.detailFit === "cover"
  ) {
    const ratio = framing.detailAspectRatio ?? 1.1;
    return (
      <Box
        sx={{
          width: `min(100%, calc((100dvh - 160px) * ${ratio}))`,
          aspectRatio: String(ratio),
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          onLoad={mediaObservation.ready}
          src={url}
          alt={item.label}
          onError={() => {
            setFailed(true);
            mediaObservation.failed();
          }}
          sx={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: `scale(${framing.detailZoom ?? 1})`,
            transformOrigin: `${(framing.detailFocalX ?? 0.5) * 100}% ${(framing.detailFocalY ?? 0.5) * 100}%`,
            objectPosition: `${(framing.detailFocalX ?? 0.5) * 100}% ${(framing.detailFocalY ?? 0.5) * 100}%`,
            touchAction: "pan-y",
          }}
        />
      </Box>
    );
  }
  if (url)
    return (
      <Box
        component="img"
        onLoad={mediaObservation.ready}
        src={url}
        alt={item.label}
        onError={() => {
          setFailed(true);
          mediaObservation.failed();
        }}
        sx={{
          display: "block",
          width: "100%",
          height: detail ? "auto" : 200,
          maxHeight: detail ? "calc(100dvh - 160px)" : undefined,
          objectFit: detail ? "contain" : "cover",
          touchAction: detail ? "pan-y" : undefined,
        }}
      />
    );
  return (
    <Stack
      sx={{ height: 200, p: 2, alignItems: "center", justifyContent: "center" }}
    >
      <Typography>{item.label}</Typography>
      {item.type === "video" ? <VideocamRounded /> : <MicRounded />}
    </Stack>
  );
}

/** Separate dialog state never navigates or submits the underlying survey. */
export function FeedbackMediaGallery({
  open,
  onClose,
  items,
  initialIndex,
  configuration,
  locale,
}: {
  open: boolean;
  onClose: () => void;
  items: ProofOfServiceMediaItem[];
  initialIndex: number;
  configuration: Configuration;
  locale?: string;
}) {
  const { gallery, tokens, localization } = configuration;
  const theme = useTheme();
  const id = useId();
  const local = (path: string, fallback: string) =>
    (locale && localization.supportedLocales.includes(locale)
      ? localization.translations[locale]?.[path]
      : undefined) ??
    localization.translations[localization.defaultLocale]?.[path] ??
    fallback;
  const text = (key: string, fallback: string) =>
    local(
      `text/gallery.${key}/value`,
      configuration.content.text[`gallery.${key}`] ?? fallback,
    );
  const [tab, setTab] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const previousOpen = useRef(false);
  const tabs = gallery.tabs.length
    ? gallery.tabs
    : [{ type: "all" as const, label: text("all", "All media") }];
  const activeTab = Math.min(tab, tabs.length - 1);
  const visible = items
    .map((item, index) => ({ item, index }))
    .filter(
      ({ item }) =>
        item.type !== "note" &&
        (tabs[activeTab]?.type === "all" ||
          mediaType(item) === tabs[activeTab]?.type),
    );
  const detailIndex = visible.findIndex((entry) => entry.index === selected);
  const active = detailIndex >= 0 ? visible[detailIndex] : undefined;
  useEffect(() => {
    if (open && !previousOpen.current && selected === null) {
      const type = items[initialIndex]
        ? mediaType(items[initialIndex])
        : undefined;
      const initialTab = tabs.findIndex(
        (entry) => entry.type === type || entry.type === "all",
      );
      setTab(Math.max(0, initialTab));
    }
    if (!open) setDetailOpen(false);
    previousOpen.current = open;
  }, [open, initialIndex, items, tabs]);
  const navigate = (delta: number) => {
    if (visible.length > 1)
      setSelected(
        visible[(detailIndex + delta + visible.length) % visible.length].index,
      );
  };
  const closeDetail = () => setDetailOpen(false);
  const title = text("title", "All Media Files");
  const unavailable = text("unavailable", "This media could not be loaded.");
  const close =
    local("configuration/gallery/closeLabel", gallery.closeLabel).trim() ||
    "Close";
  return (
    <>
      <Dialog
        open={open && gallery.enabled}
        onClose={onClose}
        aria-labelledby={id}
        dir={localization.direction}
        maxWidth={false}
        slotProps={{
          container: { sx: { alignItems: "flex-end" } },
          paper: {
            sx: {
              m: 0,
              width: "100%",
              maxWidth: 444,
              height: "80dvh",
              maxHeight: "80dvh",
              borderRadius: "16px 16px 0 0",
              bgcolor: tokens.surface,
              color: tokens.text,
              fontFamily: theme.typography.fontFamily,
              "& .MuiTypography-root,& .MuiButtonBase-root": {
                fontFamily: theme.typography.fontFamily,
              },
            },
          },
          backdrop: { sx: { bgcolor: "rgba(0,0,0,0.48)" } },
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 32,
            height: 4,
            minHeight: 4,
            mx: "auto",
            mt: "4px",
            borderRadius: 30,
            bgcolor: "rgba(145,158,171,0.32)",
          }}
        />
        <DialogTitle
          id={id}
          sx={{
            color: "inherit",
            fontSize: 17,
            fontWeight: 600,
            textAlign: "center",
            pt: 1,
            pb: 2,
            px: 6,
          }}
        >
          {title}
        </DialogTitle>
        <IconButton
          aria-label={close}
          onClick={onClose}
          sx={{
            position: "absolute",
            insetInlineEnd: 4,
            top: 8,
            width: 44,
            height: 44,
            color: tokens.text,
          }}
        >
          <CloseRounded sx={{ fontSize: 20 }} />
        </IconButton>
        <DialogContent
          sx={{ px: 2, pt: 0, pb: 2, overscrollBehavior: "contain" }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, value) => setTab(value)}
            variant="fullWidth"
            aria-label={title}
            sx={{
              minHeight: 36,
              mb: 1.5,
              borderBottom: "2px solid rgba(145,158,171,0.08)",
              "& .MuiTabs-indicator": { bgcolor: tokens.text },
              "& .MuiTab-root": {
                minWidth: 0,
                minHeight: 36,
                p: 0.5,
                textTransform: "none",
                fontSize: 14,
                fontWeight: 500,
                bgcolor: "transparent",
                color: tokens.textMuted,
                "&:hover": { bgcolor: "transparent" },
              },
              "& .MuiTab-root.Mui-selected": {
                color: tokens.text,
                fontWeight: 600,
              },
            }}
          >
            {tabs.map((entry, index) => (
              <Tab
                key={`${entry.type}-${index}`}
                id={`${id}-tab-${index}`}
                aria-controls={`${id}-panel`}
                label={local(
                  `configuration/gallery/tabs/${entry.type}`,
                  entry.label,
                )}
                iconPosition="start"
                icon={
                  entry.type === "image" ? (
                    <Box
                      component="span"
                      aria-hidden="true"
                      data-testid="gallery-photo-glyph"
                      sx={{
                        width: 20,
                        height: 20,
                        bgcolor: "currentColor",
                        mask: "url(/assets/survey/gallery-photo.svg) center / contain no-repeat",
                        WebkitMask:
                          "url(/assets/survey/gallery-photo.svg) center / contain no-repeat",
                      }}
                    />
                  ) : entry.type === "video" || entry.type === "audio" ? (
                    <Box
                      component="span"
                      aria-hidden="true"
                      sx={{
                        width: 20,
                        height: 20,
                        bgcolor: "currentColor",
                        mask: `url(${JSON.stringify(entry.type === "video" ? galleryVideo : galleryVoice)}) center / contain no-repeat`,
                        WebkitMask: `url(${JSON.stringify(entry.type === "video" ? galleryVideo : galleryVoice)}) center / contain no-repeat`,
                      }}
                    />
                  ) : undefined
                }
              />
            ))}
          </Tabs>
          <Box
            role="tabpanel"
            id={`${id}-panel`}
            aria-labelledby={`${id}-tab-${activeTab}`}
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr)",
              gap: 1.5,
            }}
          >
            {visible.map(({ item, index }) => (
              <ButtonBase
                key={`${index}:${item.url}:${item.thumbnailUrl}`}
                aria-label={text("open", "Open {label}").replace(
                  "{label}",
                  item.label,
                )}
                aria-current={selected === index ? true : undefined}
                onClick={() => {
                  setSelected(index);
                  setDetailOpen(true);
                }}
                sx={{
                  display: "block",
                  borderRadius: "12px",
                  overflow: "hidden",
                  width: "100%",
                  "&:focus-visible": {
                    outline: `2px solid ${tokens.text}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <GalleryAsset
                  item={item}
                  detail={false}
                  unavailable={unavailable}
                />
              </ButtonBase>
            ))}
            {visible.length === 0 && (
              <Typography
                role="status"
                sx={{ gridColumn: "1/-1", py: 3, textAlign: "center" }}
              >
                {text("empty", "No media in this category.")}
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        fullScreen
        open={open && gallery.enabled && detailOpen && Boolean(active)}
        onClose={closeDetail}
        aria-labelledby={`${id}-detail`}
        dir={localization.direction}
        slotProps={{
          paper: {
            sx: {
              bgcolor: "#000",
              color: "#fff",
              overflow: "hidden",
              position: "relative",
              fontFamily: theme.typography.fontFamily,
              "& .MuiTypography-root,& .MuiButtonBase-root": {
                fontFamily: theme.typography.fontFamily,
              },
            },
          },
        }}
        onKeyDown={(event) => {
          if (
            ["VIDEO", "AUDIO", "INPUT"].includes(
              (event.target as HTMLElement).tagName,
            )
          )
            return;
          if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
            event.preventDefault();
            navigate(
              (event.key === "ArrowRight" ? 1 : -1) *
                (localization.direction === "rtl" ? -1 : 1),
            );
          }
        }}
      >
        <DialogTitle
          id={`${id}-detail`}
          sx={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            clipPath: "inset(50%)",
            p: 0,
          }}
        >
          {text("detail", "Media viewer")}
        </DialogTitle>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            height: 58,
            insetInline: 0,
            justifyContent: "center",
            position: "absolute",
            top: 0,
            zIndex: 2,
          }}
        >
          <ButtonBase
            onClick={closeDetail}
            aria-label={close}
            sx={{
              position: "absolute",
              insetInlineStart: 4,
              top: 7,
              width: 44,
              height: 44,
              borderRadius: 1,
              "&:focus-visible": { outline: "2px solid white" },
            }}
          >
            <Box
              component="img"
              src="/assets/survey/gallery-close.svg"
              alt=""
              sx={{ width: 20, height: 20 }}
            />
          </ButtonBase>
          <Typography
            sx={{ fontSize: 15, fontWeight: 600, lineHeight: "26px" }}
          >
            {detailIndex + 1}/{visible.length}
          </Typography>
        </Stack>
        <Stack
          data-testid="gallery-media-stage"
          sx={{
            alignItems: "center",
            inset: 0,
            justifyContent: "center",
            overflow: "hidden",
            position: "absolute",
          }}
        >
          {detailOpen && active?.item.type === "image" ? (
            <ZoomableGalleryImage
              key={`${active.index}:${active.item.url}`}
              item={active.item}
              framing={gallery}
              unavailable={unavailable}
              onNavigate={(delta) =>
                navigate(delta * (localization.direction === "rtl" ? -1 : 1))
              }
            />
          ) : detailOpen && active ? (
            <GalleryAsset
              key={`${active.index}:${active.item.url}`}
              item={active.item}
              detail
              framing={gallery}
              unavailable={unavailable}
            />
          ) : null}
        </Stack>
      </Dialog>
    </>
  );
}
